import { Badge, Button, Card, CardContent, CardHeader, CardTitle, cn } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  FileCheck2,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import {
  createPlanDraft,
  getContentPacks,
  listGeneratedTasks,
  listPlanInstances,
  publishPlan,
  recurrenceLabel,
  updatePlanCard,
  type PlanCardContent,
  type PlanInstance,
  type PlanPack,
} from "./plan-api.js";

function packForInstance(packs: readonly PlanPack[], instance: PlanInstance): PlanPack | undefined {
  return packs.find((pack) => pack.code === instance.plan_pack_code);
}

function CardDecision({
  card,
  decision,
  disabled,
  onDecide,
}: {
  card: PlanCardContent;
  decision: PlanInstance["cards"][number];
  disabled: boolean;
  onDecide: (applicable: boolean) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-stone-100 bg-stone-50/70 px-5 py-4 sm:px-7">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Badge>{card.category.replaceAll("_", " ")}</Badge>
          <span className="text-xs font-semibold text-stone-400">{card.code}</span>
        </div>
      </div>
      <CardContent className="p-5 sm:p-7">
        <h3 className="text-xl font-black tracking-tight text-stone-950 sm:text-2xl">{card.applicability_question}</h3>
        <p className="mt-3 text-sm leading-6 text-stone-600">{card.summary_md ?? card.procedure_requirement}</p>
        <div className="mt-7 grid gap-3 sm:grid-cols-2">
          <button
            aria-pressed={decision.applicable}
            className={cn(
              "flex min-h-24 items-center gap-4 rounded-2xl border-2 p-4 text-left transition",
              decision.applicable ? "border-emerald-800 bg-emerald-50 text-emerald-950" : "border-stone-200 bg-surface text-stone-700 hover:border-stone-300",
            )}
            disabled={disabled}
            onClick={() => onDecide(true)}
            type="button"
          >
            <span className={cn("grid size-9 shrink-0 place-items-center rounded-full", decision.applicable ? "bg-emerald-800 text-white" : "bg-stone-100 text-stone-500")}><Check className="size-4" /></span>
            <span><span className="block font-black">Yes, this applies</span><span className="mt-1 block text-xs leading-5 opacity-70">Include this card and its evidence requirements.</span></span>
          </button>
          <button
            aria-pressed={!decision.applicable}
            className={cn(
              "flex min-h-24 items-center gap-4 rounded-2xl border-2 p-4 text-left transition",
              !decision.applicable ? "border-stone-700 bg-stone-100 text-stone-950" : "border-stone-200 bg-surface text-stone-700 hover:border-stone-300",
            )}
            disabled={disabled}
            onClick={() => onDecide(false)}
            type="button"
          >
            <span className={cn("grid size-9 shrink-0 place-items-center rounded-full", !decision.applicable ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-500")}><X className="size-4" /></span>
            <span><span className="block font-black">No, not here</span><span className="mt-1 block text-xs leading-5 opacity-70">Keep it outside this location’s published plan.</span></span>
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function Questionnaire({
  instance,
  pack,
  onReview,
}: {
  instance: PlanInstance;
  onReview: () => void;
  pack: PlanPack;
}) {
  const queryClient = useQueryClient();
  const questions = pack.cards.filter((card) => card.applicability_question !== null);
  const [index, setIndex] = useState(0);
  const [visited, setVisited] = useState(() => new Set<string>());
  const update = useMutation({
    mutationFn: ({ applicable, card }: { applicable: boolean; card: PlanCardContent }) =>
      updatePlanCard(instance.id, {
        applicable,
        card_code: card.code,
        reason: applicable ? null : "Not used at this location",
      }),
    onSuccess: async (updated, variables) => {
      queryClient.setQueryData<PlanInstance[]>(["plan-instances", instance.location_id], (current) =>
        current?.map((entry) => entry.id === updated.id ? updated : entry),
      );
      setVisited((current) => new Set(current).add(variables.card.code));
    },
  });
  const card = questions[index];
  const decision = instance.cards.find((entry) => entry.card_code === card?.code);
  const percent = questions.length === 0 ? 100 : Math.round(((index + 1) / questions.length) * 100);

  if (!card || !decision) {
    return <ErrorState message="The draft and content pack have different card versions." title="This questionnaire needs attention" />;
  }

  return (
    <div className="space-y-5">
      <Card className="overflow-hidden bg-emerald-800 text-white">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-[0.65rem] font-bold tracking-widest text-emerald-300 uppercase">Applicability questionnaire</p><p className="mt-1 text-sm font-bold">Question {index + 1} of {questions.length}</p></div><Badge className="bg-surface/10 text-emerald-100">Draft v{instance.version_no}</Badge></div>
          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-surface/15"><div className="h-full rounded-full bg-emerald-300 transition-all" style={{ width: `${percent}%` }} /></div>
        </CardContent>
      </Card>

      <CardDecision card={card} decision={decision} disabled={update.isPending} onDecide={(applicable) => update.mutate({ applicable, card })} />
      {update.error ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" role="alert">{displayError(update.error)}</p> : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button disabled={index === 0 || update.isPending} onClick={() => setIndex((current) => Math.max(0, current - 1))} variant="outline"><ArrowLeft className="size-4" /> Previous</Button>
        <p className="text-xs font-semibold text-stone-500">{visited.size > 0 ? `${visited.size} choice${visited.size === 1 ? "" : "s"} saved this visit` : "The recommended defaults are already saved"}</p>
        {index < questions.length - 1 ? (
          <Button disabled={update.isPending} onClick={() => setIndex((current) => Math.min(questions.length - 1, current + 1))}>Next <ArrowRight className="size-4" /></Button>
        ) : (
          <Button disabled={update.isPending} onClick={onReview}>Review plan <ClipboardList className="size-4" /></Button>
        )}
      </div>
    </div>
  );
}

function PlanReview({
  instance,
  onBack,
  onPublished,
  pack,
}: {
  instance: PlanInstance;
  onBack: () => void;
  onPublished: () => void;
  pack: PlanPack;
}) {
  const queryClient = useQueryClient();
  const publish = useMutation({
    mutationFn: () => publishPlan(instance.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["plan-instances", instance.location_id] });
      onPublished();
    },
  });
  const contentByCode = new Map(pack.cards.map((card) => [card.code, card]));
  const included = instance.cards.filter((card) => card.applicable);
  const excluded = instance.cards.filter((card) => !card.applicable);
  const categories = [...new Set(included.map((card) => contentByCode.get(card.card_code)?.category ?? "other"))];
  const recordCount = included.reduce((count, card) => count + (contentByCode.get(card.card_code)?.records.length ?? 0), 0);

  return (
    <div className="space-y-5">
      <Card className="overflow-hidden">
        <div className="grid gap-px bg-stone-200 sm:grid-cols-3">
          {[{ label: "Included cards", value: included.length }, { label: "Excluded cards", value: excluded.length }, { label: "Record obligations", value: recordCount }].map((metric) => (
            <div className="bg-surface p-5" key={metric.label}><p className="text-2xl font-black text-stone-950">{metric.value}</p><p className="mt-1 text-xs font-semibold text-stone-500">{metric.label}</p></div>
          ))}
        </div>
      </Card>
      <Card>
        <CardHeader><p className="text-[0.65rem] font-bold tracking-widest text-emerald-800 uppercase">Final review</p><CardTitle className="text-xl">Cards included in this plan</CardTitle><p className="text-sm leading-5 text-stone-500">Publishing makes this version effective and generates the kitchen schedules that follow from it.</p></CardHeader>
        <CardContent className="space-y-5 pt-3">
          {categories.map((category) => (
            <section key={category}>
              <h3 className="text-xs font-black tracking-wider text-stone-400 uppercase">{category.replaceAll("_", " ")}</h3>
              <div className="mt-2 divide-y divide-stone-100 rounded-xl border border-stone-200">
                {included.filter((card) => (contentByCode.get(card.card_code)?.category ?? "other") === category).map((card) => {
                  const content = contentByCode.get(card.card_code);
                  return <div className="flex gap-3 px-4 py-3.5" key={card.card_code}><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-700" /><div><p className="text-sm font-bold">{card.title}</p>{content?.records.length ? <p className="mt-1 text-xs text-stone-500">{content.records.map((record) => record.record_requirement).join(" · ")}</p> : null}</div></div>;
                })}
              </div>
            </section>
          ))}
        </CardContent>
      </Card>
      {publish.error ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" role="alert">{displayError(publish.error)}</p> : null}
      <div className="flex flex-wrap justify-between gap-3"><Button disabled={publish.isPending} onClick={onBack} variant="outline"><ArrowLeft className="size-4" /> Back to questions</Button><Button disabled={publish.isPending} onClick={() => { if (globalThis.confirm("Publish this Food Control Plan? It becomes the active evidence plan for this location.")) publish.mutate(); }}>{publish.isPending ? "Publishing…" : "Publish plan"} <FileCheck2 className="size-4" /></Button></div>
    </div>
  );
}

function PublishedPlan({
  instance,
  locationId,
  onCreateDraft,
  pack,
}: {
  instance: PlanInstance;
  locationId: string;
  onCreateDraft: () => void;
  pack: PlanPack;
}) {
  const tasks = useQuery({
    queryFn: () => listGeneratedTasks(locationId),
    queryKey: ["generated-plan-tasks", locationId, instance.id],
    refetchInterval: (query) => query.state.data?.length ? false : 5_000,
  });
  const applicable = instance.cards.filter((card) => card.applicable).length;

  return (
    <div className="space-y-5">
      <Card className="overflow-hidden bg-emerald-800 text-white">
        <CardContent className="relative p-6 sm:p-8">
          <div className="absolute -top-12 -right-12 size-48 rounded-full bg-emerald-400/10 blur-2xl" aria-hidden="true" />
          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
            <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-emerald-300 text-emerald-950"><BookOpenCheck className="size-6" /></span>
            <div className="min-w-0 flex-1"><p className="text-[0.65rem] font-bold tracking-widest text-emerald-300 uppercase">Published Food Control Plan</p><h2 className="mt-2 text-2xl font-black tracking-tight">{pack.title}</h2><p className="mt-2 text-sm text-emerald-100/70">Version {instance.version_no} · {applicable} applicable cards · effective {instance.effective_from ? new Intl.DateTimeFormat("en-NZ", { dateStyle: "medium" }).format(new Date(instance.effective_from)) : "now"}</p></div>
            <Button className="bg-surface text-emerald-950 hover:bg-emerald-50" onClick={onCreateDraft}>Review or change plan</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0"><div><CardTitle>Generated schedules</CardTitle><p className="mt-1 text-sm leading-5 text-stone-500">These recurring tasks were created from the published plan for manager review.</p></div><Button aria-label="Refresh schedules" disabled={tasks.isFetching} onClick={() => void tasks.refetch()} size="icon" variant="ghost"><RefreshCw className={cn("size-4", tasks.isFetching && "animate-spin")} /></Button></CardHeader>
        <CardContent className="pt-3">
          {tasks.isPending ? <LoadingState label="Preparing generated schedules" /> : null}
          {tasks.error ? <ErrorState message={displayError(tasks.error)} onAction={() => void tasks.refetch()} title="Schedules aren’t available" /> : null}
          {tasks.data?.length === 0 ? <EmptyState calm description="The worker is generating schedules from this plan. You can leave this screen; kitchen work is never blocked." title="Schedules are being prepared" /> : null}
          {tasks.data && tasks.data.length > 0 ? <div className="divide-y divide-stone-100 rounded-xl border border-stone-200">{tasks.data.map((task) => <div className="flex items-center gap-3 px-4 py-4" key={task.id}><span className="grid size-9 shrink-0 place-items-center rounded-xl bg-stone-100 text-stone-600"><CalendarClock className="size-4" /></span><div className="min-w-0 flex-1"><p className="truncate text-sm font-bold">{task.title}</p><p className="mt-1 text-xs text-stone-500">{recurrenceLabel(task.schedule?.rrule)}{task.schedule?.due_window_start ? ` · from ${task.schedule.due_window_start}` : ""}</p></div><Badge tone={task.status === "active" ? "positive" : "neutral"}>{task.status}</Badge></div>)}</div> : null}
        </CardContent>
      </Card>
    </div>
  );
}

export function PlanWorkspace({ canManage, locationId }: { canManage: boolean; locationId: string }) {
  const queryClient = useQueryClient();
  const [reviewing, setReviewing] = useState(false);
  const packs = useQuery({ queryFn: getContentPacks, queryKey: ["content-packs"], staleTime: 300_000 });
  const instances = useQuery({ queryFn: () => listPlanInstances(locationId), queryKey: ["plan-instances", locationId] });
  const draft = instances.data?.find((instance) => instance.state === "draft");
  const published = instances.data?.find((instance) => instance.state === "published");
  const allPacks = packs.data?.packs ?? [];
  const activePacks = packs.data?.packs.filter((pack) => pack.status === "active") ?? [];
  const selectedPack = draft ? packForInstance(allPacks, draft) : published ? packForInstance(allPacks, published) : activePacks[0];
  const create = useMutation({
    mutationFn: ({ clone, packCode }: { clone: boolean; packCode: string }) => createPlanDraft({ clone_current: clone, location_id: locationId, plan_pack_code: packCode }),
    onSuccess: async () => {
      setReviewing(false);
      await queryClient.invalidateQueries({ queryKey: ["plan-instances", locationId] });
    },
  });

  useEffect(() => setReviewing(false), [locationId]);

  if (!canManage) return <EmptyState description="A manager or administrator maintains the published plan for this location." title="Plan setup is manager-only" />;
  if (packs.isPending || instances.isPending) return <LoadingState label="Loading the Food Control Plan" />;
  if (packs.error || instances.error) return <ErrorState message={displayError(packs.error ?? instances.error)} onAction={() => { void packs.refetch(); void instances.refetch(); }} title="The Food Control Plan isn’t available" />;
  if (!selectedPack) return <ErrorState message="No active New Zealand Food Control Plan content pack is installed." title="Plan content is unavailable" />;

  if (draft) {
    return reviewing ? <PlanReview instance={draft} onBack={() => setReviewing(false)} onPublished={() => setReviewing(false)} pack={selectedPack} /> : <Questionnaire instance={draft} onReview={() => setReviewing(true)} pack={selectedPack} />;
  }
  if (published) {
    return <PublishedPlan instance={published} locationId={locationId} onCreateDraft={() => create.mutate({ clone: true, packCode: activePacks[0]?.code ?? selectedPack.code })} pack={selectedPack} />;
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="grid min-h-96 place-items-center p-7 text-center sm:p-10">
        <div className="max-w-xl"><span className="mx-auto grid size-14 place-items-center rounded-2xl bg-emerald-100 text-emerald-900"><Sparkles className="size-6" /></span><p className="mt-5 text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">{selectedPack.authority}</p><h2 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">Build {selectedPack.title}</h2><p className="mt-3 text-sm leading-6 text-stone-600">Answer practical questions about what this kitchen does. SafePlate starts with the recommended defaults, then shows every included card before anything is published.</p><div className="mt-5 flex flex-wrap justify-center gap-2"><Badge>{selectedPack.version_label}</Badge><Badge tone="positive">New Zealand</Badge>{selectedPack.source_url ? <a className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800" href={selectedPack.source_url} rel="noreferrer" target="_blank">View source <ExternalLink className="size-3" /></a> : null}</div>{create.error ? <p className="mt-5 text-sm font-semibold text-red-800" role="alert">{displayError(create.error)}</p> : null}<Button className="mt-7" disabled={create.isPending} onClick={() => create.mutate({ clone: false, packCode: selectedPack.code })}>{create.isPending ? "Starting…" : "Start questionnaire"} <ArrowRight className="size-4" /></Button></div>
      </CardContent>
    </Card>
  );
}
